<template>
  <div class="lectures-page">
    <div v-if="teacherId && classroomId">
      <div class="lectures-columns-view">
        <div class="columns lecture-columns">
          <LecturesColumn
            class="column lecture-column"
            :curriculum="selectedCurriculum"
            :lectures="lectures"
            :get-topic-by-id="getTopicById"
            :canEdit="true"
            :curriculums="curriculums"
            :classroomId="classroomId"
            @drop="handleLectureDrop"
            @full="handleLectureFull"
            @edit="editLecture"
            @clear="handleClearLecture"
            @lecture-updated="loadLectures"
            @select-topic="handleSelectTopic"
            @remove-topic="removeLectureTopic"
            @curriculum-created="handleCurriculumCreated"
          />

          <TopicList
            class="column topic-column drop-zone"
            :topics="topics"
            :selectedTopicId="selectedTopicId"
            :topicUsageMap="topicUsageMap"
            :enableDragDrop="true"
            :includeTopicEditor="false"
            :curriculums="curriculums"
            @topics-updated="loadTopics"
            @drop="handleTopicsDrop"
            @select-topic="handleSelectTopic"
            @preview-topic="handlePreviewTopic"
          />

          <div class="column">
            <TopicPreview
              :topic="selectedTopic"
              :scenarios="scenarios"
              :canEdit="false"
            />
          </div>
        </div>

        <b-modal
          v-model="isLectureEditorActive"
          has-modal-card
          trap-focus
          :destroy-on-hide="true"
        >
          <LectureEditor
            v-if="lectureToEdit"
            :lecture="lectureToEdit"
            @close="isLectureEditorActive = false"
            @save="handleLectureSaved"
          />
        </b-modal>
      </div>
    </div>
    <div v-else>Please select a class</div>
  </div>
</template>

<script>
import {
  CurriculumService,
  TopicsService,
  LectureService,
} from "@/services/CoachingServices";
import LecturesColumn from "../LecturesColumn.vue";
import LectureEditor from "../LectureEditor.vue";
import TopicList from "../../explorer/organisms/TopicList.vue";
import TopicPreview from "../../explorer/organisms/TopicPreview.vue";
import topicScenarioLoader from "@/mixins/topicScenarioLoader";
import { mapGetters } from "vuex";
import SACFrontendService from "@/services/SACFrontendService";

export default {
  name: "LecturesPageIndex",

  components: {
    TopicList,
    LecturesColumn,
    LectureEditor,
    TopicPreview,
  },

  mixins: [topicScenarioLoader],

  data() {
    return {
      curriculums: [],
      selectedCurriculum: null,
      selectedTopicId: null,
      topics: [],
      lectures: [],
      isLectureEditorActive: false,
      lectureToEdit: null,
    };
  },

  computed: {
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),

    teacherId() {
      return SACFrontendService.teacherInfo()?.teacherId;
    },

    classroomId() {
      return this.currentClass?.classId;
    },

    topicUsageMap() {
      if (!this.lectures?.length) {
        return new Map();
      }

      const mapping = new Map();
      this.lectures.forEach((lecture, index) => {
        lecture.topicIds.forEach((topicId) => {
          mapping.set(topicId, `w${index + 1}`);
        });
      });
      return mapping;
    },

    selectedTopic() {
      if (!this.selectedTopicId) return null;
      return (
        this.topics.find((topic) => topic.id === this.selectedTopicId) || null
      );
    },
  },

  watch: {
    teacherId: {
      immediate: true,
      handler() {
        this.loadData();
      },
    },

    classroomId: {
      immediate: true,
      handler() {
        this.loadData();
      },
    },
  },

  methods: {
    async loadCurriculums() {
      try {
        this.curriculums = await CurriculumService.getCurriculums();
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to load curriculums: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    async handleCurriculumCreated(curriculumId) {
      await this.loadCurriculums();
      this.selectedCurriculum =
        this.curriculums.find((c) => c.id === curriculumId) || null;
    },

    async loadTopics() {
      try {
        this.topics = await TopicsService.getPublicAndUserTopics(
          this.teacherId
        );
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to load topics: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    handleSelectTopic(topicId) {
      if (this.selectedTopicId === topicId) return;
      this.selectedTopicId = topicId;
    },

    async loadLectures() {
      try {
        this.lectures = await LectureService.getByClassId(this.classroomId);
      } catch (error) {
        this.$buefy.toast.open({
          message: `Failed to load lectures: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    getTopicById(id) {
      return this.topics.find((t) => t.id === id);
    },

    /*
     * TopicsColumn handlers
     */

    async handleTopicsDrop(event) {
      const topicData = JSON.parse(event.dataTransfer.getData("text/plain"));
      console.log("handleTopicsDrop", topicData);
      const topicId = topicData.id;

      this.removeLectureTopic(topicId);
    },

    /*
     * LecturesColumn handlers
     */

    async handleLectureDrop({ topicId, index }) {
      try {
        const lecture = this.lectures[index];
        if (!lecture) return;

        // remove topicId from other lectures
        this.lectures.forEach((l) => {
          if (l.id !== lecture.id) {
            l.removeTopic(topicId);
          }
        });

        lecture.addTopic(topicId);
        await LectureService.updateLecture(lecture.id, lecture);

        const topic = this.getTopicById(topicId);
        this.$buefy.toast.open({
          message: `Topic "${topic.title}" added to lecture ${lecture.weekNumber}`,
          type: "is-success",
        });

        this.$emit("lecture-updated");
      } catch (error) {
        console.error("Drop handling error:", error);
        this.$buefy.toast.open({
          message: "Failed to add topic to lecture",
          type: "is-danger",
        });
      }
    },

    handleLectureFull({ index }) {
      this.$buefy.toast.open({
        message: `Week ${index + 1} is full`,
        type: "is-danger",
      });
    },

    async handleClearLecture(lectureId) {
      const lecture = this.lectures.find((l) => l.id === lectureId);
      if (!lecture) return;

      lecture.topicIds = [];
      await LectureService.updateLecture(lecture.id, lecture);

      this.$emit("lecture-updated");
    },

    async removeLectureTopic(topicId) {
      console.log("removeLectureTopic", topicId);
      try {
        // Remove topic from all lectures
        const updatedLectures = this.lectures.filter((lecture) => {
          return lecture.topicIds.includes(topicId);
        });

        // Update each lecture that contained the topic
        await Promise.all(
          updatedLectures.map(async (lecture) => {
            lecture.removeTopic(topicId);
            await LectureService.updateLecture(lecture.id, lecture);
          })
        );

        if (updatedLectures.length > 0) {
          const topic = this.getTopicById(topicId);
          this.$buefy.toast.open({
            message: `Topic "${topic.title}" removed from lectures`,
            type: "is-success",
          });

          await this.loadLectures();
        }
      } catch (error) {
        console.error("Drop handling error:", error);
        this.$buefy.toast.open({
          message: "Failed to remove topic from lectures",
          type: "is-danger",
        });
      }
    },

    editLecture(lecture) {
      this.lectureToEdit = lecture;
      this.isLectureEditorActive = true;
    },

    async handleLectureSaved({ lectureId, weekNumber, lectureDate }) {
      try {
        const lecture = this.lectures.find((l) => l.id === lectureId);
        if (!lecture) return;

        lecture.update({
          weekNumber,
          lectureDate,
        });

        await LectureService.updateLecture(lecture.id, lecture);

        this.$buefy.toast.open({
          message: "Lecture updated successfully",
          type: "is-success",
        });

        await this.loadLectures();
      } catch (error) {
        console.error("Failed to update lecture:", error);
        this.$buefy.toast.open({
          message: "Failed to update lecture",
          type: "is-danger",
        });
      }
    },

    async loadData() {
      console.log("loadData", this.teacherId, this.classroomId);
      if (!this.teacherId || !this.classroomId) {
        return;
      }

      await this.loadCurriculums();
      await this.loadTopics();
      await this.loadLectures();
    },
  },
};
</script>

<style scoped>
.lecture-columns {
  gap: 1rem;
  margin: 0;
  height: 85vh;
}

.lecture-columns > div {
  border: 4px solid #dbdbdb;
  padding: 0;
  margin: 0 !important; /* Override Bulma column margins */
}

/* Ensure columns have equal width */
.lecture-columns > div.column {
  flex: 1;
}

.lecture-column,
.topic-column {
  max-width: 400px;
}

.drop-zone.is-dragover {
  background-color: rgba(0, 209, 178, 0.1);
  border: 4px dashed #00d1b2;
}
</style>
